import { useEffect, useRef, useState } from "react";
import { createSseClient, type SseConnectionState } from "../lib/sseClient";
import type { AdminAlertEvent } from "../schemas/anomalyResolution";

/**
 * UC-007 Phase 20 admin alerts SSE hook.
 *
 * Konzumuje `/api/admin/alerts/stream` event stream. Pull fallback (REQ-UI-024)
 * — pokud SSE disconnect, periodicky refresh `/api/admin/anomalies` (pull URL
 * spravuje volající přes refreshState helper z mateřského komponentu).
 *
 * Per NFR-014 (R-035 mitigation gate): kritické alerty MUSÍ být doručeny < 5 min.
 * Server emit < 1 ms; SSE roundtrip + UI render ~ 1 s = pohodlně pod limit.
 */

export interface AdminAlertSnapshot {
  /** All received alerts (descending — newest first). */
  alerts: AdminAlertEvent[];
  connection: SseConnectionState;
  lastError: string | null;
}

const EMPTY_SNAPSHOT: AdminAlertSnapshot = {
  alerts: [],
  connection: "connecting",
  lastError: null,
};

export interface UseAdminAlertsSseOptions {
  url?: string;
  /** Optional pull-fallback URL (e.g. /api/admin/anomalies) for disconnect recovery. */
  pullFallbackUrl?: string;
  pullIntervalMs?: number;
  disablePull?: boolean;
  /** Max alerts kept in memory (default 50 — UI banner shows top 5). */
  maxAlerts?: number;
}

export function useAdminAlertsSse(opts: UseAdminAlertsSseOptions = {}): {
  snapshot: AdminAlertSnapshot;
  /** Manually trigger pull fallback (e.g. after SSE reconnect). */
  refreshFallback: () => Promise<void>;
} {
  const {
    url = "/api/admin/alerts/stream",
    pullFallbackUrl,
    pullIntervalMs = 30_000,
    disablePull = false,
    maxAlerts = 50,
  } = opts;

  const [snapshot, setSnapshot] = useState<AdminAlertSnapshot>(EMPTY_SNAPSHOT);
  const refreshRef = useRef<() => Promise<void>>(async () => {});

  // Pull fallback — invoked when SSE drops.
  useEffect(() => {
    if (!pullFallbackUrl) {
      refreshRef.current = async () => {};
      return;
    }
    let cancelled = false;
    const refresh = async () => {
      try {
        const resp = await fetch(pullFallbackUrl, { credentials: "include" });
        if (!resp.ok || cancelled) return;
        // Pull endpoint returns AnomalyListResponse; we don't push it through
        // the SSE alert pipeline (different shape), but the call is enough to
        // signal liveness — caller refreshes its own list state.
      } catch {
        // ignore — periodic retry
      }
    };
    refreshRef.current = refresh;
    if (disablePull) return;
    const id = setInterval(refresh, pullIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [pullFallbackUrl, pullIntervalMs, disablePull]);

  // SSE event stream.
  useEffect(() => {
    const handle = createSseClient({
      url,
      extraEvents: ["alert"],
      onEvent: (eventName, data) => {
        if (eventName !== "alert" && eventName !== "message") return;
        const event = data as AdminAlertEvent;
        if (!event || event.kind !== "anomaly") return;
        setSnapshot((prev) => ({
          ...prev,
          alerts: [event, ...prev.alerts].slice(0, maxAlerts),
          lastError: null,
        }));
      },
      onConnectionStateChange: (connState) => {
        setSnapshot((prev) => ({ ...prev, connection: connState }));
      },
      onError: () => {
        setSnapshot((prev) => ({ ...prev, lastError: "sse_error" }));
        void refreshRef.current?.();
      },
    });
    return () => handle.close();
  }, [url, maxAlerts]);

  return { snapshot, refreshFallback: () => refreshRef.current() };
}
